import { ReactNode, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { AlertTriangle, CheckCircle2, Database, Loader2, Plug, Plus, Trash2 } from "lucide-react";
import { Button } from "../../ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
    DatabaseCredentialTestResult,
    deleteDatabaseCredential,
    fetchDatabaseCredentials,
    fetchDatabaseInstances,
    saveDatabaseCredential,
    SecretDiscoveryDatabaseCredential,
    SecretDiscoveryDatabaseEngine,
    SecretDiscoveryDatabaseInstance,
    testDatabaseCredential,
} from "../CloudAnalytics/secretDiscoveryService";

export const DATABASE_CREDENTIALS_KEY = "secret-discovery-database-credentials";
export const DATABASE_INSTANCES_KEY = "secret-discovery-database-instances";

const ENGINE_LABELS: Record<SecretDiscoveryDatabaseEngine, string> = {
    POSTGRES: "PostgreSQL",
    MYSQL: "MySQL",
    SQLSERVER: "SQL Server",
};

const DEFAULT_PORTS: Record<SecretDiscoveryDatabaseEngine, number> = {
    POSTGRES: 5432,
    MYSQL: 3306,
    SQLSERVER: 1433,
};

interface DraftState {
    instanceName: string;
    engine: SecretDiscoveryDatabaseEngine;
    databaseName: string;
    username: string;
    password: string;
    passwordSecretRef: string;
    host: string;
    port: string;
}

const EMPTY_DRAFT: DraftState = {
    instanceName: "",
    engine: "POSTGRES",
    databaseName: "",
    username: "",
    password: "",
    passwordSecretRef: "",
    host: "",
    port: "",
};

function Field({
    label,
    hint,
    children,
}: {
    label: string;
    hint?: string;
    children: ReactNode;
}) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-foreground">{label}</Label>
            {children}
            {hint ? <span className="text-[11px] text-muted-foreground">{hint}</span> : null}
        </div>
    );
}

function CredentialRow({
    credential,
    onDelete,
    isDeleting,
}: {
    credential: SecretDiscoveryDatabaseCredential;
    onDelete: () => void;
    isDeleting: boolean;
}) {
    const [result, setResult] = useState<DatabaseCredentialTestResult | null>(null);
    const testMutation = useMutation(() => testDatabaseCredential(credential.id), {
        onSuccess: setResult,
        onError: () => setResult({ reachable: false, message: "Unable to reach the database" }),
    });

    return (
        <div className="rounded-xl border border-border bg-white p-3 dark:bg-card">
            <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Database className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                        {credential.instanceName} · {credential.databaseName}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                        {ENGINE_LABELS[credential.engine]} · {credential.username}
                        {credential.passwordSecretRef
                            ? ` · secret ${credential.passwordSecretRef}`
                            : credential.hasStoredPassword ? " · stored password" : ""}
                    </div>
                </div>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1.5 rounded-lg text-xs"
                    disabled={testMutation.isLoading}
                    onClick={() => testMutation.mutate()}
                >
                    {testMutation.isLoading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <Plug className="h-3.5 w-3.5" />
                    )}
                    Test
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 rounded-lg p-0 text-muted-foreground hover:text-foreground"
                    disabled={isDeleting}
                    onClick={onDelete}
                    aria-label="Remove credential"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            </div>

            {result ? (
                <div
                    className={`mt-2 flex items-start gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] ${
                        result.reachable
                            ? "bg-primary/10 text-primary"
                            : "bg-muted text-muted-foreground"
                    }`}
                >
                    {result.reachable ? (
                        <CheckCircle2 className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    ) : (
                        <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    )}
                    <span>{result.reachable ? "Connected successfully" : result.message}</span>
                </div>
            ) : null}
        </div>
    );
}

export function DatabaseCredentialDialog({
    open,
    onOpenChange,
    cloudProvider,
    tenantName,
    projectId,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    cloudProvider: string;
    tenantName?: string;
    projectId?: string;
}) {
    const queryClient = useQueryClient();
    const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
    const [formError, setFormError] = useState<string | null>(null);
    const enabled = open && Boolean(tenantName) && Boolean(projectId);

    const { data: credentials = [], isLoading: areCredentialsLoading } = useQuery(
        [DATABASE_CREDENTIALS_KEY, cloudProvider, tenantName, projectId],
        () => fetchDatabaseCredentials(cloudProvider, tenantName, projectId),
        { enabled, refetchOnWindowFocus: false },
    );

    const { data: instances = [] } = useQuery(
        [DATABASE_INSTANCES_KEY, cloudProvider, tenantName, projectId],
        () => fetchDatabaseInstances(cloudProvider, tenantName, projectId),
        { enabled, refetchOnWindowFocus: false },
    );

    const unregistered = useMemo(
        () => instances.filter((instance) => !instance.registered),
        [instances],
    );

    useEffect(() => {
        if (!open) {
            setDraft(EMPTY_DRAFT);
            setFormError(null);
        }
    }, [open]);

    const invalidate = () => {
        queryClient.invalidateQueries([DATABASE_CREDENTIALS_KEY]);
        queryClient.invalidateQueries([DATABASE_INSTANCES_KEY]);
    };

    const saveMutation = useMutation(
        () =>
            saveDatabaseCredential(cloudProvider, {
                tenantName: tenantName as string,
                projectId: projectId as string,
                instanceName: draft.instanceName.trim(),
                engine: draft.engine,
                databaseName: draft.databaseName.trim(),
                username: draft.username.trim(),
                password: draft.password || undefined,
                passwordSecretRef: draft.passwordSecretRef.trim() || undefined,
                host: draft.host.trim() || undefined,
                port: draft.port ? Number(draft.port) : undefined,
            }),
        {
            onSuccess: () => {
                setDraft(EMPTY_DRAFT);
                setFormError(null);
                invalidate();
            },
            onError: (error: any) => setFormError(error?.message || "Unable to save the credential"),
        },
    );

    const deleteMutation = useMutation(
        (credentialId: string) => deleteDatabaseCredential(credentialId),
        { onSuccess: invalidate },
    );

    const selectInstance = (instance: SecretDiscoveryDatabaseInstance) => {
        setDraft((current) => ({
            ...current,
            instanceName: instance.instanceName,
            engine: instance.engine ?? current.engine,
            host: instance.host ?? "",
            port: String(DEFAULT_PORTS[instance.engine ?? current.engine]),
        }));
    };

    const canSave =
        draft.instanceName.trim().length > 0
        && draft.databaseName.trim().length > 0
        && draft.username.trim().length > 0
        && (draft.password.length > 0 || draft.passwordSecretRef.trim().length > 0)
        && !saveMutation.isLoading;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="text-base">Database credentials</DialogTitle>
                    <DialogDescription className="text-xs">
                        Scanning a database needs a read-only login. Values are matched by hash, so
                        no secret is ever sent to the database.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    {areCredentialsLoading ? (
                        <div className="py-4 text-center text-xs text-muted-foreground">
                            Loading credentials...
                        </div>
                    ) : credentials.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-border py-6 text-center text-xs text-muted-foreground">
                            No databases are registered for scanning yet.
                        </div>
                    ) : (
                        credentials.map((credential) => (
                            <CredentialRow
                                key={credential.id}
                                credential={credential}
                                isDeleting={deleteMutation.isLoading}
                                onDelete={() => deleteMutation.mutate(credential.id)}
                            />
                        ))
                    )}
                </div>

                <div className="mt-2 rounded-xl border border-border p-4">
                    <div className="mb-3 text-sm font-semibold text-foreground">Add a database</div>

                    {unregistered.length > 0 ? (
                        <div className="mb-3 flex flex-wrap gap-1.5">
                            {unregistered.slice(0, 8).map((instance) => (
                                <button
                                    key={instance.instanceName}
                                    type="button"
                                    onClick={() => selectInstance(instance)}
                                    className={`rounded-lg border px-2 py-1 text-[11px] transition-colors ${
                                        draft.instanceName === instance.instanceName
                                            ? "border-primary/40 bg-primary/10 text-primary"
                                            : "border-border text-muted-foreground hover:bg-muted/60"
                                    }`}
                                >
                                    {instance.instanceName}
                                </button>
                            ))}
                        </div>
                    ) : null}

                    <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="Instance">
                            <Input
                                value={draft.instanceName}
                                onChange={(event) =>
                                    setDraft({ ...draft, instanceName: event.target.value })}
                                placeholder="my-sql-instance"
                                className="h-9 text-sm"
                            />
                        </Field>
                        <Field label="Engine">
                            <select
                                value={draft.engine}
                                onChange={(event) =>
                                    setDraft({
                                        ...draft,
                                        engine: event.target.value as SecretDiscoveryDatabaseEngine,
                                        port: String(DEFAULT_PORTS[
                                            event.target.value as SecretDiscoveryDatabaseEngine]),
                                    })}
                                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                            >
                                {Object.entries(ENGINE_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Database">
                            <Input
                                value={draft.databaseName}
                                onChange={(event) =>
                                    setDraft({ ...draft, databaseName: event.target.value })}
                                placeholder="appdb"
                                className="h-9 text-sm"
                            />
                        </Field>
                        <Field label="Username">
                            <Input
                                value={draft.username}
                                onChange={(event) =>
                                    setDraft({ ...draft, username: event.target.value })}
                                placeholder="whiteswan_readonly"
                                className="h-9 text-sm"
                            />
                        </Field>
                        <Field label="Host" hint="Leave empty to use the synced instance address">
                            <Input
                                value={draft.host}
                                onChange={(event) => setDraft({ ...draft, host: event.target.value })}
                                placeholder="10.0.0.5"
                                className="h-9 text-sm"
                            />
                        </Field>
                        <Field label="Port">
                            <Input
                                value={draft.port}
                                onChange={(event) => setDraft({ ...draft, port: event.target.value })}
                                placeholder={String(DEFAULT_PORTS[draft.engine])}
                                className="h-9 text-sm"
                            />
                        </Field>
                        <Field
                            label="Password"
                            hint="Encrypted at rest and never returned by the API"
                        >
                            <Input
                                type="password"
                                value={draft.password}
                                onChange={(event) =>
                                    setDraft({ ...draft, password: event.target.value })}
                                className="h-9 text-sm"
                            />
                        </Field>
                        <Field
                            label="Or cloud secret name"
                            hint="A secret this scan already reads, so nothing new is stored"
                        >
                            <Input
                                value={draft.passwordSecretRef}
                                onChange={(event) =>
                                    setDraft({ ...draft, passwordSecretRef: event.target.value })}
                                placeholder="db-password"
                                className="h-9 text-sm"
                            />
                        </Field>
                    </div>

                    {formError ? (
                        <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-muted px-2.5 py-1.5 text-[11px] text-muted-foreground">
                            <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                            <span>{formError}</span>
                        </div>
                    ) : null}

                    <Button
                        type="button"
                        size="sm"
                        className="mt-4 h-9 w-full gap-1.5 rounded-lg text-xs font-semibold"
                        disabled={!canSave}
                        onClick={() => saveMutation.mutate()}
                    >
                        {saveMutation.isLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Plus className="h-3.5 w-3.5" />
                        )}
                        Save credential
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
